"use client"
import { Flip } from "gsap/Flip"
import { gsap } from "gsap"
import { useGSAP } from "@gsap/react"
import { CustomEase } from "gsap/CustomEase"
import { useRef } from "react"
import SplitType from "split-type"

// Register plugins
gsap.registerPlugin(CustomEase, Flip, useGSAP)

export default function Hero() {
  const workIntro = useRef<HTMLDivElement>(null)
  const r1Ref = useRef<HTMLDivElement>(null)
  const r2Ref = useRef<HTMLDivElement>(null)
  const revealersRef = useRef<HTMLDivElement>(null)

  useGSAP(
    () => {
      // Confirm refs are ready
      if (!workIntro.current || !r1Ref.current || !r2Ref.current) return

      // Optional delay to ensure layout is ready
      gsap.delayedCall(0, () => {
        CustomEase.create("hop", "M0,0 C0.355,0.022 0.448,0.079 0.5,0.5 0.542,0.846 0.615,1 1,1")
        CustomEase.create("hop2", "M0,0 C0.078,0.617 0.114,0.716 0.255,0.828 0.373,0.922 0.561,1 1,1")

        const splitH3 = new SplitType(".site-info h3", { types: "lines" })

        splitH3.lines?.forEach(line => {
          const text = line.textContent
          const wrapper = document.createElement("div")
          wrapper.className = "line"
          const span = document.createElement("span")
          span.textContent = text
          wrapper.appendChild(span)
          line.parentNode?.replaceChild(wrapper, line)
        })

        const mainTl = gsap.timeline()
        const revealerTl = gsap.timeline()
        const scaleTl = gsap.timeline()

        revealerTl
          .to(r1Ref.current, {
            clipPath: "polygon(0% 0%, 100% 0%, 100% 0%, 0% 0%)",
            delay: 1.5,
            ease: "hop"
          })
          .to(
            r2Ref.current,
            {
              clipPath: "polygon(0% 100%, 100% 100%, 100% 100%, 0% 100%)",
              duration: 1.5,
              ease: "hop"
            },
            "<"
          )

        scaleTl.to(".img:first-child", {
          scale: 1,
          duration: 2,
          ease: "power4.inOut"
        })

        const images = document.querySelectorAll(".img:not(:first-child)")
        images.forEach(img => {
          scaleTl.to(
            img,
            {
              opacity: 1,
              scale: 1,
              duration: 1.25,
              ease: "power3.out"
            },
            ">-0.5"
          )
        })

        mainTl
          .add(revealerTl)
          .add(scaleTl, "-=1.25")
          .add(() => {
            const state = Flip.getState(".main")
            const imagesContainer = workIntro.current!.querySelector(".images")

            workIntro.current!.querySelectorAll(".img:not(.main)").forEach(img => img.remove())
            imagesContainer?.classList.add("stacked-container")

            workIntro.current!.querySelectorAll(".main").forEach((img, i) => {
              img.classList.add("stacked")
              img.style.order = i.toString()
              gsap.set(".img.stacked", {
                clearProps: "transform, top, left"
              })
            })

            return Flip.from(state, {
              duration: 2,
              ease: "hop",
              absolute: true,
              stagger: { amount: -0.3 }
            })
          })
          .to("h1, .nav-item p, .line p, .site-info h3 .line span, .site-info p", {
            y: 0,
            duration: 3,
            ease: "hop2",
            stagger: 0.1,
            delay: 1.25,
            clipPath: "polygon(0% 100%, 100% 100%, 100% 0%, 0% 0%)",
            opacity: 1
          })
          .to(".cover-img", {
            clipPath: "polygon(0% 100%, 100% 100%, 100% 0%, 0% 0%)",
            duration: 1.75,
            ease: "hop",
            delay: -3.75,
            onComplete: () => {
              sessionStorage.setItem("playAnimation", "true")
            }
          })

        // Cleanup
        return () => {
          gsap.killTweensOf(workIntro.current!)
          splitH3.revert()

          workIntro.current?.querySelectorAll(".stacked").forEach(img => {
            img.classList.remove("stacked")
            img.style.order = ""
          })

          const imagesContainer = workIntro.current?.querySelector(".images")
          imagesContainer?.classList.remove("stacked-container")

          if (revealersRef.current) {
            revealersRef.current.style.display = "none"
          }
        }
      })
    },
    { scope: workIntro }
  )


  return (
    <section className="linear-green-yellow">
      <div className="animation-container" ref={workIntro}>
        <div ref={revealersRef} className="revealers">
          <div className="revealer r1" ref={r1Ref}></div>
          <div className="revealer r2" ref={r2Ref}></div>
        </div>
        <div className="container">
          <div className="mt-32 m-0 p-0 row max-w-screen-md">
            <h1 className="work-title pb-0">Work</h1>
            <div className="site-info">
              <p className="p-0 m-0">Here are examples of where and how we&apos;ve added value to our clients.</p>
            </div>
          </div>

          <div className="images">
            <div className="img">
              <img src="/work/therapy-featured.jpg" alt="" />
            </div>
            <div className="img main">
              <img src="/work/eclosia-featured.jpg" alt="" />
            </div>
            <div className="img main">
              <img src="/work/wpo-featured.jpg" alt="" />
            </div>
            <div className="img main">
              <img src="/work/agorize-featured.jpg" alt="" />
            </div>
          </div>

          <div className="cover-img">
            <img src="/work/therapy-featured.jpg" alt="" className="aspect-auto" />
          </div>
        </div>
      </div>
    </section>
  )
}
